import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";

const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword) {
      user { id }
      errors
    }
  }
`;

export function ChangePasswordPage() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (vars: { currentPassword: string; newPassword: string }) =>
      graphqlClient.request<{
        changePassword: { user: { id: string } | null; errors: string[] };
      }>(CHANGE_PASSWORD_MUTATION, vars),
    onSuccess: (data) => {
      if (data.changePassword.user) {
        setErrors([]);
        setSuccess(true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setErrors(data.changePassword.errors);
        setSuccess(false);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setErrors([t("settings.change_password.passwords_mismatch")]);
      return;
    }

    mutation.mutate({ currentPassword, newPassword });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 flex items-center gap-3">
        <Link
          to="/profile/edit"
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {t("settings.title")}
        </Link>
        <span className="text-sm text-gray-400">/</span>
        <h1 className="text-2xl font-bold text-gray-900">
          {t("settings.change_password.title")}
        </h1>
      </div>

      {errors.length > 0 && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {errors.map((err, i) => (
            <p key={i}>{err}</p>
          ))}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-lg bg-green-50 p-3 text-sm text-green-700">
          {t("settings.change_password.success")}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-2 border-b border-gray-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <label htmlFor="currentPassword" className="text-sm font-semibold text-gray-900">
              {t("settings.change_password.current_password")}
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input sm:w-72"
              required
            />
          </div>

          <div className="flex flex-col gap-2 border-b border-gray-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <label htmlFor="newPassword" className="text-sm font-semibold text-gray-900">
              {t("settings.change_password.new_password")}
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input sm:w-72"
              required
              minLength={6}
            />
          </div>

          <div className="flex flex-col gap-2 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <label htmlFor="confirmPassword" className="text-sm font-semibold text-gray-900">
              {t("settings.change_password.confirm_password")}
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input sm:w-72"
              required
              minLength={6}
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary"
          >
            {mutation.isPending
              ? t("settings.change_password.submitting")
              : t("settings.change_password.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
